import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { Box, Portal, TextField } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import moment from 'moment'

import 'moment/locale/pl'

import { ReviewHistory } from '../api/index'
import ButtonBox from '../components/ButtonBox'
import Section from '../components/Section'
import SectionHeader from '../components/SectionHeader'
import useAxiosPrivate from '../hooks/useAxiosPrivate'
import {
  ArrowLeft,
  ArrowRight,
  CancelIcon,
  CheckIcon,
  HourglassIcon,
  SaveIcon,
} from '../icons'

function ControlsEditInspection() {
  const { controlId, portalRef, onCancelClick } = useOutletContext()
  const axiosPrivate = useAxiosPrivate()
  const navigate = useNavigate()
  const [state, setState] = useState({
    lastTechnicalReviewDate: null,
    nextTechnicalReviewDate: null,
    technicalReviewExpirationDate: null,
  })
  const [sendBtnLoading, setSendBtnLoading] = useState(false)

  useEffect(() => {
    ReviewHistory.getReviewHistory(axiosPrivate, controlId)
      .then(({ data }) => setState(data))
      // eslint-disable-next-line no-console
      .catch((e) => console.error(e))
  }, [axiosPrivate, controlId])

  const saveReviewHistory = (redirect) => (e) => {
    e.preventDefault()
    setSendBtnLoading(true)
    ReviewHistory.setReviewHistory(axiosPrivate, controlId, state)
      .then(() => {
        if (redirect) {
          navigate(redirect)
        }
      })
      // eslint-disable-next-line no-console
      .catch((er) => console.error(er))
      .finally(() => {
        setSendBtnLoading(false)
      })
  }

  const { t } = useTranslation()

  return (
    <div>
      <Section>
        <SectionHeader
          icon={<HourglassIcon />}
          title={t('inspectionTitle')}
          subtitle={t('inspectionSubTitle')}
        />

        <Box>
          <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="pl">
            <DatePicker
              label={t('inspectionLabel1')}
              inputFormat="DD-MM-YYYY"
              value={state.lastTechnicalReviewDate}
              onChange={(newValue) => {
                setState((prevState) => ({
                  ...prevState,
                  lastTechnicalReviewDate: moment.utc(newValue).format(),
                }))
              }}
              renderInput={(params) => (
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  sx={{ mt: 3 }}
                  {...params}
                />
              )}
            />
            <DatePicker
              label={t('inspectionLabel2')}
              inputFormat="DD-MM-YYYY"
              value={state.nextTechnicalReviewDate}
              onChange={(newValue) => {
                setState((prevState) => ({
                  ...prevState,
                  nextTechnicalReviewDate: moment.utc(newValue).format(),
                }))
              }}
              renderInput={(params) => (
                <TextField
                  InputLabelProps={{ shrink: true }}
                  sx={{ mt: 3 }}
                  fullWidth
                  {...params}
                />
              )}
            />
            <DatePicker
              label={t('inspectionLabel3')}
              inputFormat="DD-MM-YYYY"
              value={state.technicalReviewExpirationDate}
              onChange={(newValue) => {
                setState((prevState) => ({
                  ...prevState,
                  technicalReviewExpirationDate: moment.utc(newValue).format(),
                }))
              }}
              renderInput={(params) => (
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  sx={{ mt: 3 }}
                  {...params}
                />
              )}
            />
          </LocalizationProvider>
        </Box>
        <Portal container={portalRef.current}>
          <ButtonBox
            buttons={[
              {
                label: 'Wstecz',
                icon: <ArrowLeft fill="white" />,
                props: {
                  variant: 'contained',
                  color: 'primary',
                  loading: sendBtnLoading,
                  onClick: saveReviewHistory(
                    `/controls/${controlId}/edit/guarantee`,
                  ),
                },
              },
              {
                label: t('backToListBtnLabel'),
                icon: <CancelIcon />,
                props: {
                  variant: 'outlined',
                  color: 'primary',
                  onClick: onCancelClick,
                },
              },
              {
                label: 'Zapisz',
                icon: <SaveIcon fill="white" />,
                props: {
                  variant: 'contained',
                  color: 'primary',
                  loading: sendBtnLoading,
                  onClick: saveReviewHistory(),
                },
              },
              {
                label: 'Zakończ',
                icon: <CheckIcon fill="white" />,
                props: {
                  variant: 'contained',
                  color: 'primary',
                  loading: sendBtnLoading,
                  onClick: saveReviewHistory(`/controls/${controlId}/summary`),
                },
              },
              {
                label: t('nextBtnLabel'),
                icon: <ArrowRight fill="white" />,
                props: {
                  variant: 'contained',
                  color: 'primary',
                  loading: sendBtnLoading,
                  onClick: saveReviewHistory(
                    `/controls/${controlId}/edit/bodyCondition`,
                  ),
                },
              },
            ]}
          />
        </Portal>
      </Section>
    </div>
  )
}

export default ControlsEditInspection
